import {TeamReviewIdentity, TeamReviewEvidence, TeamModelValues} from "./TeamReviewContext";
import TeamDecisionReview from "./LegacyTeamDecisionReview";
import {decisionField} from "../helpers/LegacyteamDecision";
import TeamLeagueInline from "./LegacyTeamLeagueInline";
import TeamModelReferences from "./LegacyTeamModelReferences";
import {comparableHistory,comparisonMetrics} from "../helpers/teamWorkspace";
import TeamSourceComparison from "./TeamSourceComparison";
import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  TextField,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  createWorkareaTheme,
  readWorkareaMode,
  saveWorkareaMode,
} from "../helpers/workareaTheme";
import { tipsRequest } from "../helpers/tipsWorkflow";
import {
  futureSummary,
  editResult,
  selectedHistory,
  historySummary,
  statisticObservation,
  conversionObservation,
  changedFields,
} from "../helpers/LegacyteamAnalysis";
import {
  setHomeAdvantage,
  adjustHomeAdvantage,
  setDrawFactor,
  adjustDrawFactor,
  setGoalAverage,
  adjustGoalAverage,
  average,
  normalizeGoalTotals,
  normalizeHomeAdvantage,
} from "../helpers/adjustTeamStrengths";
import { prepareCompactLeagueRows } from "../helpers/compactLeagueRows";
import { fetchFixtureMarketOdds } from "./OddsTooltip";
import { getLeagueFairMarketValues } from "../helpers/leagueFairMarkets";
import { aggregateFixtureMarketOdds } from "../helpers/fixtureMarketOdds";
import { workspaceIndications } from "../helpers/workspaceIndications";
import "./LegacyTeamAnalysisDialog.css";
import {
  statisticSuggestion,
  teamSuggestions,
  neutralResult,
} from "../helpers/LegacyteamAdjustment";
const EditingContext = React.createContext(true);
const FOUR = ["home_for", "home_against", "away_for", "away_against"];
function ValueInput({
  value,
  onChange,
  label,
  disabled = false,
  precision = 3,
  active = false,
  displayLabel,
  onActivate,
}) {
  const editingAllowed = React.useContext(EditingContext);
  const display = () =>
    value == null ? "" : String(Number(value.toFixed(precision)));
  const [draft, setDraft] = useState(display),
    changed = useRef(false);
  useEffect(() => {
    setDraft(display());
    changed.current = false;
  }, [value, precision]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <TextField
      className={active ? "ta-edit-target" : undefined}
      size="small"
      type="number"
      label={displayLabel||label}
      value={draft}
      disabled={!editingAllowed || disabled || value == null}
      inputProps={{ step: 10 ** -precision, "aria-label": label }}
      onFocus={() => {
        onActivate?.();
        setDraft(display());
        changed.current = false;
      }}
      onChange={(e) => {
        setDraft(e.target.value);
        changed.current = true;
      }}
      onBlur={() => {
        if (changed.current && draft !== "" && Number(draft) !== value)
          onChange(Number(draft));
        else setDraft(display());
        changed.current = false;
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
      }}
    />
  );
}
export default function TeamAnalysisDialog({
  workspace = false,
  league,
  seasonId,
  teamIds,
  fixture = null,
  indicationThreshold,
  onClose,
  onSaved,
  onStart,
  request = tipsRequest,
}) {
  const { i18n } = useTranslation(),
    en = i18n.language.startsWith("en"),
    t = (de, eng) => (en ? eng : de);
  const [decisionLevel,setDecisionLevel]=useState('summary'),[decisionSide,setDecisionSide]=useState('for');
  function selectDecisionLevel(level){setDecisionLevel(level);if(level==='composition')setVenue('all');if(level==='venue'&&venue==='all')setVenue('home');}
  const [decisionModel,setDecisionModel]=useState('result'),[decisionMetric,setDecisionMetric]=useState('total_strength'),[explanationIntent,setExplanationIntent]=useState({count:0});
  const [editing,setEditing]=useState(!workspace);
  const [mode, setMode] = useState(readWorkareaMode),
    [data, setData] = useState(null),
    [draft, setDraft] = useState({}),
    [edits, setEdits] = useState([]),
    [stat, setStat] = useState(null),
    [breakdown, setBreakdown] = useState({}),
    [rank, setRank] = useState([]),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [preview, setPreview] = useState(null),
    [tour, setTour] = useState(-1),
    [focus, setFocus] = useState(null),
    [lastSix, setLastSix] = useState(false),
    [recentCount,setRecentCount]=useState(8),
    [shortShow,setShortShow]=useState(true),
    [outcomeMode,setOutcomeMode]=useState("count"),
    [totals, setTotals] = useState(true),
    [venue, setVenue] = useState("all"),
    [confirmClose, setConfirmClose] = useState(false),
    [leagueModel, setLeagueModel] = useState("result"),
    [leagueAction, setLeagueAction] = useState("scale"),
    [leagueMetric, setLeagueMetric] = useState("goals"),
    [target, setTarget] = useState("3.1"),
    [saved, setSaved] = useState(false);
  const scope = {
      league_id: Number(league.id),
      season_id: Number(seasonId || league.current_season_id),
    },
    seq = useRef(0);
  const fmt = (n, d = 2) =>
    typeof n === "number" && Number.isFinite(n)
      ? n.toLocaleString(en ? "en-GB" : "de-DE", {
          maximumFractionDigits: Math.min(d, 3),
          minimumFractionDigits: d === 0 ? 0 : Math.min(d, 2),
        })
      : "—";
  const trField = (k) =>
    ({
      total_strength: t("Spielstärke · Quotient", "Strength · ratio"),
      total_goals: t("Torschnitt", "Goals per game"),
      for_total: t("Für · Gesamt", "For · overall"),
      against_total: t("Gegen · Gesamt", "Against · overall"),
      for_home: t("Heim · Für", "Home · for"),
      against_home: t("Heim · Gegen", "Home · against"),
      for_away: t("Auswärts · Für", "Away · for"),
      against_away: t("Auswärts · Gegen", "Away · against"),
      for_homefactor: t("Offensiver Heimfaktor", "Attacking home factor"),
      against_homefactor: t("Defensiver Heimfaktor", "Defensive home factor"),
      draw_factor: t("Remisfaktor", "Draw factor"),
      drawfactor: t("Liga-Remisfaktor", "League draw factor"),
      stored_goal_average: t(
        "Gespeicherter Liga-Torschnitt",
        "Stored league goal average"
      ),
      exp_home_factor: t(
        "Erwarteter Liga-Heimfaktor",
        "Expected league home factor"
      ),
      home_for: t("Heim · Für", "Home · for"),
      home_against: t("Heim · Gegen", "Home · against"),
      away_for: t("Auswärts · Für", "Away · for"),
      away_against: t("Auswärts · Gegen", "Away · against"),
    }[k] || k);
  const call = (mode, body = {}) =>
    request("statistic-model", {
      ...scope,
      team_ids: teamIds.map(Number),
      mode,
      ...body,
    });
  async function load() {
    setBusy(true);
    setError("");
    try {
      const d = await call("team-dialog", {
        team_ids: teamIds.map(Number),
        fixture_id: fixture?.id,
      });
      setData(d);
      setDraft(d.result_teams);
      setStat(d.state);
      setBreakdown(d.breakdown || {});
      setRank(d.ranking);
      setEdits([]);
      setPreview(null);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    load();
    return () => {
      // This is a request-generation counter, not a DOM ref. Invalidate pending previews on unmount.
      seq.current++; // eslint-disable-line react-hooks/exhaustive-deps
    };
  }, [scope.league_id, scope.season_id, teamIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const resultChanged = data
    ? Object.keys(draft).filter(
        (id) =>
          JSON.stringify(draft[id]) !== JSON.stringify(data.result_teams[id])
      )
    : [];
  const dirty = resultChanged.length > 0 || edits.length > 0;
  useEffect(()=>{if(decisionLevel==='venue'&&venue==='all')setVenue('home')},[decisionLevel,venue]);
  useEffect(()=>{
    const guard=e=>{if(dirty&&!window.confirm(t("Ungespeicherte Änderungen verwerfen und den Bereich verlassen?","Discard unsaved changes and leave this area?")))e.preventDefault();};
    const unload=e=>{if(dirty){e.preventDefault();e.returnValue="";}};
    window.addEventListener('betty:before-navigation',guard);window.addEventListener('beforeunload',unload);
    return()=>{window.removeEventListener('betty:before-navigation',guard);window.removeEventListener('beforeunload',unload);};
  },[dirty,en]); // eslint-disable-line react-hooks/exhaustive-deps
  const names = (id) =>
    data?.result_teams?.[id]?.team_name ||
    data?.observations.find((f) => f.home_team_id === Number(id))?.home_name ||
    data?.observations.find((f) => f.away_team_id === Number(id))?.away_name ||
    t("Mannschaft", "Team");
  function logo(id){
    const f=data?.observations?.find(f=>[f.home_team_id,f.away_team_id].includes(Number(id)));
    const src=data?.team_images?.[id] || (f?.home_team_id===Number(id)?f?.home_image_path:f?.away_image_path);
    return src?<img className="ta-logo" src={src} alt="" onError={e=>{e.currentTarget.style.display='none';}}/>:<span className="ta-logo-fallback" aria-hidden="true">⚽</span>;
  }
  function matchList(id){
    const games=selectedHistory(data.observations,id,recentCount).filter(f=>venue==='all'||f[venue+'_team_id']===Number(id));
    return <section className="ta-recent"><h4>{t(`Letzte ${recentCount} Spiele · ${names(id)}`,`Last ${recentCount} matches · ${names(id)}`)}</h4><div className="ta-scroll"><table><thead><tr>{[t("Datum / Paarung","Date / fixture"),t("Spielergebnis","Final score"),t("Ergebnismodell · erwartet","Results model · expected"),t("Statistikmodell · erwartet","Statistics model · expected"),'Should Goals',t("Δ Tordifferenz zum Ergebnismodell","Δ goal difference vs results model")].map(x=><th key={x}>{x}</th>)}</tr></thead><tbody>{games.map(f=>{
      const home=f.home_team_id===Number(id),sign=home?1:-1,d=(f.goals.home-f.goals.away)*sign;
      const delta=f.result?.xg?d-(f.result.xg.home-f.result.xg.away)*sign:null;
      const score=p=>p?`${fmt(p.home)} : ${fmt(p.away)}`:'—';
      return <tr key={f.id}><td><small>{new Date(f.kickoff).toLocaleDateString(en?'en-GB':'de-DE')}</small><div className="ta-fixture">{logo(f.home_team_id)}{f.home_name}<span>–</span>{logo(f.away_team_id)}{f.away_name}</div></td><td><span className={'ta-result '+(d>0?'win':d<0?'loss':'draw')} title={d>0?t('Sieg','Win'):d<0?t('Niederlage','Loss'):t('Unentschieden','Draw')}>{d>0?t('S','W'):d<0?t('N','L'):t('U','D')}</span> {fmt(f.goals.home,0)}:{fmt(f.goals.away,0)}</td><td>{score(f.result?.xg)}</td><td>{score(f.statistics_model?.xg)}</td><td>{score(f.should)}</td><td className={delta>0?'ta-positive':delta<0?'ta-negative':''}>{delta>0?'+':''}{fmt(delta)}</td></tr>;
    })}</tbody></table></div></section>;
  }
  function edit(id, field, value) {
    if(!editing){setError(t("Zum Ändern zuerst Bearbeiten wählen.","Choose Edit before changing settings."));return;}
    try {
      setDraft({ ...draft, [id]: editResult(draft[id], field, value) });
      setPreview(null);
      setSaved(false);
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }
  async function statEdit(e) {
    if(!editing){setError(t("Zum Ändern zuerst Bearbeiten wählen.","Choose Edit before changing settings."));return;}
    const n = ++seq.current,
      next = [...edits, ...(Array.isArray(e) ? e : [e])];
    setBusy(true);
    setPreview(null);
    setSaved(false);
    try {
      const p = await call("team-settings-preview", {
        edits: next,
        expected_revision: data.state.revision,
      });
      if (n === seq.current) {
        setEdits(next);
        setStat(p.state);
        setBreakdown(p.breakdown || {});
        setRank(p.ranking);
        setError("");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      if (n === seq.current) setBusy(false);
    }
  }
  function neutralize(id, model, r) {
    if(!editing){setError(t("Zum Ändern zuerst Bearbeiten wählen.","Choose Edit before changing settings."));return;}
    if (model === "statistics")
      return statEdit({
        team_id: Number(id),
        group: "neutralize",
        field: "total_strength",
        value: r.total_strength,
        goals: r.total_goals,
      });
    try {
      setDraft({
        ...draft,
        [id]: neutralResult(draft, id, r.total_strength, r.total_goals),
      });
      setPreview(null);
      setSaved(false);
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }
  function suggestions(ids, stronger = false) {
    if(!editing){setError(t("Zum Ändern zuerst Bearbeiten wählen.","Choose Edit before changing settings."));return;}
    const proposed = teamSuggestions(data, ids, stronger, lastSix);
    if (!proposed.length) {
      setError(
        t(
          "Keine ausreichenden Vergleichsdaten für eine Anpassung.",
          "Insufficient comparable data for an adjustment."
        )
      );
      return;
    }
    // Existing preview endpoint limits each draft to 2500 operations.
    if (edits.length + proposed.length > 2500) {
      setError(
        t(
          "Bitte den Liga-Vorschlag in kleineren Teamgruppen übernehmen.",
          "Please apply the league proposal in smaller team groups."
        )
      );
      return;
    }
    return statEdit(proposed);
  }
  function strengths() {
    return resultChanged.map((id) => {
      const r = { ...draft[id] };
      for (const k of ["rank", "custom_name", "total_goals"]) delete r[k];
      return r;
    });
  }
  async function showPreview() {
    setBusy(true);
    setError("");
    try {
      const p = resultChanged.length
        ? await call("team-result-preview", {
            strengths: strengths(),
            expected_revision: data.result_revision,
          })
        : null;
      setPreview({
        parameters: p?.parametersRaw
          ? JSON.parse(p.parametersRaw)
          : data.result_parameters,
        result: resultChanged.map((id) => ({
          id,
          changes: changedFields(
            futureSummary(data.result_teams[id]),
            futureSummary(draft[id])
          ),
        })),
        statistic_fields: edits.length
          ? Object.keys(stat.teams).flatMap((id) =>
              [
                ...Object.keys(stat.teams[id].statistics).map((k) => ({
                  group: k,
                  before: data.state.teams[id]?.statistics[k],
                  after: stat.teams[id].statistics[k],
                })),
                {
                  group: "conversion",
                  before: data.state.teams[id]?.conversion,
                  after: stat.teams[id].conversion,
                },
              ].flatMap((v) =>
                changedFields(v.before, v.after).map((c) => ({
                  id,
                  group: v.group,
                  ...c,
                }))
              )
            )
          : [],
        statistics: edits.length
          ? rank
              .map((r) => ({
                id: r.team_id,
                changes: changedFields(
                  data.ranking.find((x) => x.team_id === r.team_id),
                  r
                ),
              }))
              .filter((r) => r.changes.length)
          : [],
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    if (!preview) return;
    setBusy(true);
    setError("");
    try {
      if (resultChanged.length) {
        const p = await call("team-result-save", {
          strengths: strengths(),
          expected_revision: data.result_revision,
        });
        const actual = JSON.parse(p.teamsRaw);
        setData((d) => ({
          ...d,
          result_teams: actual,
          result_revision: p.revision,
        }));
        setDraft(actual);
      }
      if (edits.length)
        await call("team-settings-save", {
          edits,
          expected_revision: data.state.revision,
        });
      const confirmed = await load();
      setSaved(!!confirmed);
      onSaved?.();
    } catch (e) {
      setPreview(null);
      setError(
        t(
          "Speicherung nicht vollständig bestätigt. Bereits bestätigte Modelländerungen bleiben erhalten. Neu laden und prüfen: ",
          "Save not fully confirmed. Confirmed model changes are retained. Reload and check: "
        ) + e.message
      );
    } finally {
      setBusy(false);
    }
  }
  const allSteps = [
    ["balance", t("Mannschaft einordnen", "Understand the team")],
    ["future", t("Beide Zukunftsmodelle", "Both future models")],
    [
      "comparison",
      t("Saison und letzte Spiele", "Season and recent matches"),
    ],
    [
      "comparison",
      t("Erwartungen, Markt und Should", "Expected, market and Should"),
    ],
    ["details", t("Heim-/Auswärtsprofil", "Home/away profile")],
    [
      "statistics",
      t(
        "Einzelstatistiken und Conversion",
        "Individual statistics and conversion"
      ),
    ],
    [
      "season",
      t("Ligaweite Season Parameters", "League-wide season parameters"),
    ],
    [
      "previous",
      t("Vorsaison und Settings prüfen", "Previous season and settings review"),
    ],
  ];
  if(workspace)allSteps.push(["comparison",t("Gemessene Leistung gegen Erwartung","Measured performance versus expectations")]);
  const stepIndices=workspace?(shortShow?[8,0,1,2,7]:[8,0,1,2,3,4,5,6,7]):shortShow?[0,1,2,3,7]:allSteps.map((_,i)=>i);
  const steps=stepIndices.map(i=>allSteps[i]);
  useEffect(() => {
    if (tour < 0) return;
    if(stepIndices[tour]===2)setLastSix(true);
    if(stepIndices[tour]===3)setLastSix(false);
    const el = document.querySelector(`[data-tour="${steps[tour][0]}"]`);
    if (!el) return;
    for(let parent=el.parentElement;parent;parent=parent.parentElement)if(parent.tagName==="DETAILS")parent.open=true;
    if (!["balance", "future"].includes(steps[tour][0])) el.querySelectorAll("details").forEach((d) => (d.open = true));
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [tour, focus]); // eslint-disable-line react-hooks/exhaustive-deps
  const activeIds = focus ? [focus] : teamIds;
  const highlight = (id) =>
    tour >= 0 && steps[tour][0] === id ? " ta-highlight" : "";
  function summaryCard(id, model) {
    const r = futureSummary(
        model === "result"
          ? draft[id]
          : rank.find((r) => r.team_id === Number(id))
      ),
      original = futureSummary(
        model === "result"
          ? data.result_teams[id]
          : data.ranking.find((r) => r.team_id === Number(id))
      );
    return (
      <article aria-label={`${names(id)} · ${model === "result" ? t("Ergebnismodell","Results model") : t("Statistikmodell","Statistics model")}`} className={"ta-model " + model + (decisionModel===model ? " ta-active-model" : " ta-reference-model")}>
        <h3>
          {model === "result"
            ? t("Ergebnismodell", "Results model")
            : t("Statistikmodell", "Statistics model")}
          <span className="ta-model-team-name">{names(id)}</span>
        </h3>
        {!r ? (
          <Alert severity="info">
            {t(
              "Persönliche Modellwerte fehlen. Keine automatische Initialisierung.",
              "Personal model values unavailable. No automatic initialization."
            )}
          </Alert>
        ) : (
          <>
            <TeamModelValues name={names(id)} modelLabel={model === "result" ? t("Ergebnismodell","Results model") : t("Statistikmodell","Statistics model")}
              original={original} current={r} en={en} renderInput={k => (
<ValueInput
                  key={k}
                  value={r[k]}
                  label={trField(k)}
                  displayLabel={k==="total_strength"?t("Quotient","Ratio"):k==="total_goals"?t("Torschnitt","Goals/game"):undefined}
                  active={decisionLevel==="summary"&&decisionModel===model&&decisionMetric===k}
                  onActivate={()=>{if(["total_strength","total_goals"].includes(k)){setDecisionModel(model);setDecisionMetric(k);setDecisionLevel("summary");}}}
                  disabled={busy||data.read_only}
                  onChange={(v) =>
                    model === "result"
                      ? edit(id, k, v)
                      : statEdit({
                          team_id: Number(id),
                          group: "summary",
                          field: k,
                          value: v,
                        })
                  }
                />
              )} />
            {decisionLevel!=="summary"&&<div className="ta-composition-inputs"><span>{decisionLevel==="composition"?t("Zusammensetzung · Gesamt","Composition · overall"):venue==="home"?t("Heimprofil","Home profile"):t("Auswärtsprofil","Away profile")}</span><div className="ta-metrics">{["for","against"].map(side=>{const k=decisionField(decisionLevel,side,venue,decisionMetric);return <ValueInput key={k} label={trField(k)} displayLabel={side==="for"?t("Für","For"):t("Gegen","Against")} value={r[k]} active={decisionModel===model&&decisionSide===side} disabled={busy||data.read_only} onActivate={()=>{setDecisionModel(model);setDecisionSide(side);}} onChange={v=>model==="result"?edit(id,k,v):statEdit({team_id:Number(id),group:"summary",field:k,value:v})}/>})}</div></div>}
            <div className="ta-stored-reference">{t("Gespeichert","Saved")}: {fmt(original?.total_strength,3)} / {fmt(original?.total_goals,3)}</div>
            <details className="ta-model-details"><summary>{t("Ableitungen, Rang & weitere Werte","Derived values, rank & more")}</summary>
            <details className="ta-model-extra"><summary>{t("Ableitungen & Heimfaktoren", "Derived values & home factors")}</summary>
            <p>
              {t("Für", "For")} <strong>{fmt(r.for_total, 4)}</strong> ·{" "}
              {t("Gegen", "Against")} <strong>{fmt(r.against_total, 4)}</strong>
            </p>
            <small>
              {t("Gespeichert", "Stored")}: {fmt(original?.total_strength, 4)} /{" "}
              {fmt(original?.total_goals, 4)} ·{" "}
              {t(
                "Heim und Auswärts gleich gewichtet",
                "Home and away equally weighted"
              )}
            </small>
            <details className="ta-factor-details"><summary>{t("Heimfaktoren bearbeiten","Edit home factors")}</summary><div className="ta-metrics">
              {["for_homefactor", "against_homefactor"].map((k) => (
                <ValueInput
                  key={k}
                  value={r[k]}
                  label={trField(k)}
                  displayLabel={k==="total_strength"?t("Quotient","Ratio"):k==="total_goals"?t("Torschnitt","Goals/game"):undefined}
                  active={decisionLevel==="summary"&&decisionModel===model&&decisionMetric===k}
                  onActivate={()=>{if(["total_strength","total_goals"].includes(k)){setDecisionModel(model);setDecisionMetric(k);setDecisionLevel("summary");}}}
                  disabled={busy||data.read_only}
                  onChange={(v) =>
                    model === "result"
                      ? edit(id, k, v)
                      : statEdit({
                          team_id: Number(id),
                          group: "summary",
                          field: k,
                          value: v,
                        })
                  }
                />
              ))}
            </div>
            </details>
            </details>
            <p className="ta-impact" aria-live="polite">
              {t("Entwurf · Quotient", "Draft · ratio")}:{" "}
              {fmt(original?.total_strength, 3)} →{" "}
              <strong>{fmt(r.total_strength, 3)}</strong>
              {" · "}
              {t("Torschnitt", "Goals per game")}:{" "}
              {fmt(original?.total_goals, 3)} →{" "}
              <strong>{fmt(r.total_goals, 3)}</strong>
              {" · "}
              {t("Modellrang", "Model rank")}:{" "}
              {model === "statistics"
                ? data.ranking.find((x) => x.team_id === Number(id))?.rank
                : Object.values(data.result_teams).filter(
                    (x) =>
                      futureSummary(x)?.total_strength >
                      original?.total_strength
                  ).length + 1}{" "}
              →{" "}
              {model === "statistics"
                ? rank.find((x) => x.team_id === Number(id))?.rank
                : Object.values(draft).filter(
                    (x) => futureSummary(x)?.total_strength > r.total_strength
                  ).length + 1}
            </p>
            <details className="ta-reset-details">
              <summary>
                {t(
                  "Aus Ligamittelwerten neu aufsetzen",
                  "Reset from league means"
                )}
              </summary>
              <p>
                {t(
                  "Ersetzt das individuelle Profil im Entwurf durch Ligamittelwerte und erreicht den oben eingestellten Quotienten und Torschnitt. Erst Speichern übernimmt die Änderung.",
                  "Replaces the individual profile in the draft with league means and reaches the ratio and goal average set above. Only Save applies the change."
                )}
              </p>
              <Button disabled={busy} onClick={() => neutralize(id, model, r)}>
                {t("Neuaufsetzen als Vorschau", "Preview reset")}
              </Button>
            </details>
            {model === "statistics" && (
              <small>
                {t(
                  "Quotient ändern erhält den Torschnitt; Torschnitt ändern erhält den Quotienten. Einzelstatistiken werden gemeinsam angepasst.",
                  "Changing the ratio preserves the goal average; changing the goal average preserves the ratio. Individual statistics are adjusted together."
                )}
              </small>
            )}
            </details>
          </>
        )}
      </article>
    );
  }
  function actualBalance(id) {
    const actual = prepareCompactLeagueRows(data.actual_table, "actual").find(
        (r) => r.teamId === Number(id)
      ),
      games = selectedHistory(data.observations, id),
      observed = historySummary(games, id, "real"),
      real =
        actual &&
        Number.isFinite(actual.played) &&
        Number.isFinite(actual.goalsScored) &&
        Number.isFinite(actual.goalsAgainst)
          ? {
              ...observed,
              count: actual.played,
              for: actual.goalsScored,
              against: actual.goalsAgainst,
              for_average:
                actual.played > 0 ? actual.goalsScored / actual.played : null,
              against_average:
                actual.played > 0 ? actual.goalsAgainst / actual.played : null,
              quotient:
                actual.goalsAgainst > 0
                  ? actual.goalsScored / actual.goalsAgainst
                  : null,
              goal_sum:
                actual.played > 0
                  ? (actual.goalsScored + actual.goalsAgainst) / actual.played
                  : null,
            }
          : observed;
    return { actual, real };
  }
  function previousSummary(id){
    const history=data.history?.teams?.find(x=>x.team_id===Number(id))?.sources?.[0];
    const promoted=data.transitions?.some(x=>x.team_id===Number(id)&&x.transition_type==='promoted');
    const n=data.newcomers?.[id];
    return <div className="ta-previous-summary"><b>{t('Vorsaison','Previous season')}{promoted?' ↑':''}</b>: {history?`${history.league_name} · ${history.season_name} · ${t('Platz','Position')} ${fmt(history.actual_totals?.position,0)} · ${history.played} ${t('Spiele','games')} · ${fmt(history.actual_totals?.points,0)} ${t('Punkte','points')} · ${fmt(history.actual_totals?.for,0)}:${fmt(history.actual_totals?.against,0)}`:t('Kein passendes Archiv verfügbar','No matching archive available')}
    {n?.source==='receiving_league_history' && <p>{t('Historischer Vergleich für Auf-/Absteiger: Spielstärke','Historical newcomer benchmark: strength')} <b>{fmt(n.strength,3)}</b> · n={n.observations}</p>}</div>;
  }
  function tourExplanation(id) {
    if(workspace&&stepIndices[tour]===8){
      const pool=comparableHistory(data.observations,id,['should','result']);
      const a=comparisonMetrics(pool.common,id,'should',true),b=comparisonMetrics(pool.common,id,'result',true);
      return pool.common.length?t(`${names(id)}: Auf ${pool.common.length} gemeinsamen Spielen beträgt die gemessene Leistung ${fmt(a.goalsFor)}:${fmt(a.goalsAgainst)} pro Spiel, erwartet wurden ${fmt(b.goalsFor)}:${fmt(b.goalsAgainst)}. Die Abweichung der Tordifferenz beträgt ${fmt(a.goalDifference-b.goalDifference)} pro Spiel. Should Goals sind unsere statistische Leistungsmessung; tatsächliche Ergebnisse fließen in diesen Vergleich nicht ein.`,`${names(id)}: Across ${pool.common.length} common matches, measured performance is ${fmt(a.goalsFor)}:${fmt(a.goalsAgainst)} per match versus expected ${fmt(b.goalsFor)}:${fmt(b.goalsAgainst)}. The goal-difference deviation is ${fmt(a.goalDifference-b.goalDifference)} per match. Should Goals are our statistical performance measure; actual results are not used in this comparison.`):t('Für Leistung gegen Erwartung fehlen gemeinsame Spiele. Keine Bewertung möglich.','No common matches are available for performance versus expectations. No assessment possible.');
    }
    const games = selectedHistory(data.observations, id),
      { actual, real } = actualBalance(id),
      recent = historySummary(
        selectedHistory(data.observations, id, recentCount),
        id,
        "real"
      ),
      r = futureSummary(draft[id]),
      s = futureSummary(rank.find((r) => r.team_id === Number(id))),
      expected = historySummary(games, id, "result"),
      market = historySummary(games, id, "market"),
      should = historySummary(games, id, "should");
    const newcomer=data.newcomers?.[id],previous=data.history?.teams?.find(x=>x.team_id===Number(id))?.sources?.[0];
    const intro=newcomer?.type==='promoted'?t(`${names(id)} ist Aufsteiger. ${previous?`Vorsaison: ${previous.league_name}, Platz ${fmt(previous.actual_totals?.position,0)}.`:''} ${newcomer.source==='receiving_league_history'?`Die gespeicherte Aufsteiger-Spielstärke dieser Liga beträgt ${fmt(newcomer.strength,3)} aus ${newcomer.observations} Beobachtungen; aktuell Ergebnis ${fmt(r?.total_strength,3)}, Statistik ${fmt(s?.total_strength,3)}.`:'Ein belegter Aufsteigervergleich fehlt.'} `,`${names(id)} is promoted. ${previous?`Previous season: ${previous.league_name}, position ${fmt(previous.actual_totals?.position,0)}.`:''} ${newcomer.source==='receiving_league_history'?`Historical newcomer strength: ${fmt(newcomer.strength,3)} from ${newcomer.observations} observations; current results ${fmt(r?.total_strength,3)}, statistics ${fmt(s?.total_strength,3)}.`:'No evidenced newcomer benchmark.'} `):'';
    return [
      intro + t(
        `${t("Platz", "Position")} ${fmt(actual?.position, 0)}, ${fmt(
          actual?.points,
          0
        )} Punkte. ${real.count} Spiele: ${fmt(real.for,0)}−${fmt(
          real.against,0
        )} Tore. Pro Spiel ${fmt(real.for_average)} für und ${fmt(
          real.against_average
        )} gegen. Quotient ${fmt(real.quotient)}, Torschnitt ${fmt(
          real.goal_sum
        )}.`,
        `Position ${fmt(actual?.position, 0)}, ${fmt(
          actual?.points,
          0
        )} points. ${real.count} games: ${fmt(real.for,0)}−${fmt(
          real.against,0
        )} goals. Per match ${fmt(real.for_average)} for and ${fmt(
          real.against_average
        )} against. Ratio ${fmt(real.quotient)}, goal average ${fmt(
          real.goal_sum
        )}.`
      ),
      t(
        `Quotient Ergebnismodell ${fmt(r?.total_strength)}, Statistikmodell ${fmt(
          s?.total_strength
        )}. Torschnitt ${fmt(r?.total_goals)} / ${fmt(
          s?.total_goals
        )}. Zukunftswerte gewichten Heim und Auswärts gleich; fehlende Modelle werden nicht ersetzt.`,
        `Results model ratio ${fmt(r?.total_strength)}, statistics ${fmt(
          s?.total_strength
        )}. Goal average ${fmt(r?.total_goals)} / ${fmt(
          s?.total_goals
        )}. Future values weight home and away equally; missing models are not substituted.`
      ),
      t(
        `Letzte ${recent.selected} Spiele: ${fmt(recent.for,0)}−${fmt(
          recent.against,0
        )}, Torschnitt ${fmt(recent.goal_sum)}. Gesamte Saison: ${fmt(
          real.goal_sum
        )}. Der Umschalter verwendet dieselben letzten ${recentCount} Partien für alle Modelle.`,
        `Last ${recent.selected} games: ${fmt(recent.for,0)}−${fmt(
          recent.against,0
        )}, goal average ${fmt(recent.goal_sum)}. Full season: ${fmt(
          real.goal_sum
        )}. The selector uses the same recent matches for every model.`
      ),
      t(
        `${names(id)}: Ergebnisquotient ${fmt(real.quotient)} gegenüber Ergebnismodell-Erwartung ${fmt(expected.quotient)}; ${real.count===expected.count&&Number.isFinite(real.quotient)&&Number.isFinite(expected.quotient)?'Abweichung '+fmt(real.quotient-expected.quotient):'Kein direkter Vergleich bei fehlenden Werten oder unterschiedlicher Abdeckung'}. Should-Quotient ${fmt(should.quotient)}, Should-Torschnitt ${fmt(should.goal_sum)} gegenüber real ${fmt(real.goal_sum)}. Abdeckung: Ergebnis ${expected.count}/${expected.selected}, Markt ${
          market.count
        }/${market.selected}, Should ${should.count}/${
          should.selected
        }. Torschnitte: ${fmt(expected.goal_sum)} / ${fmt(
          market.goal_sum
        )} / ${fmt(
          should.goal_sum
        )}. Unterschiedliche Abdeckung begrenzt den direkten Vergleich.`,
        `${names(id)}: actual ratio ${fmt(real.quotient)} versus results-model expectation ${fmt(expected.quotient)}; ${real.count===expected.count&&Number.isFinite(real.quotient)&&Number.isFinite(expected.quotient)?'difference '+fmt(real.quotient-expected.quotient):'No direct comparison with missing values or unequal coverage'}. Should ratio ${fmt(should.quotient)}, Should goal average ${fmt(should.goal_sum)} versus actual ${fmt(real.goal_sum)}. Coverage: results ${expected.count}/${expected.selected}, market ${
          market.count
        }/${market.selected}, Should ${should.count}/${
          should.selected
        }. Goal averages: ${fmt(expected.goal_sum)} / ${fmt(
          market.goal_sum
        )} / ${fmt(
          should.goal_sum
        )}. Unequal coverage limits direct comparison.`
      ),
      t(
        `Ergebnismodell: Heim für ${fmt(r?.for_home)}, auswärts für ${fmt(
          r?.for_away
        )}. Offensive Heimwirkung ${fmt(r?.for_homefactor)}, defensive ${fmt(
          r?.against_homefactor
        )}. Eine Einzeländerung erhält den anderen Spielort.`,
        `Results model: home for ${fmt(r?.for_home)}, away for ${fmt(
          r?.for_away
        )}. Attacking home factor ${fmt(r?.for_homefactor)}, defensive ${fmt(
          r?.against_homefactor
        )}. Individual edits preserve the other venue.`
      ),
      t(
        `${
          Object.keys(stat?.teams?.[id]?.statistics || {}).length
        } gespeicherte Statistiktypen. Die sichtbaren Gewichte und vier Conversion-Werte fließen über die bestehende Matchup-Berechnung in die Torwerte ein. Gesamtänderungen sind proportional; historische Mittel verwenden tatsächliche Spielezahlen.`,
        `${
          Object.keys(stat?.teams?.[id]?.statistics || {}).length
        } stored statistic types. The visible weights and four conversion values feed the existing matchup calculation. Overall changes are proportional; historical means use actual game counts.`
      ),
      t(
        `Ligaweite Parameter wirken auf alle Teams. Ergebnismodell-Torschnitt ${fmt(
          data.result_parameters?.stored_goal_average
        )}, Heimfaktor ${fmt(
          data.result_parameters?.exp_home_factor
        )}. Die Liga-Anpassung ist ausdrücklich von einer Teamkorrektur getrennt.`,
        `League parameters affect every team. Results model goal average ${fmt(
          data.result_parameters?.stored_goal_average
        )}, home factor ${fmt(
          data.result_parameters?.exp_home_factor
        )}. League changes are explicitly separate from team edits.`
      ),
      t(
        "Die Vorsaison bleibt mit ihrem Saisonbezug sichtbar. Prüfe die Abdeckung und danach die Vorschau aller Änderungen. Die Rundreise selbst speichert nichts.",
        "The previous season retains its season label. Check coverage, then preview every change. The tour itself saves nothing."
      ),
    ][stepIndices[tour]];
  }
  const labelBasis = {
    real: t("Reale Ergebnisse", "Actual results"),
    result: t(
      "Ergebnismodell · erwartet",
      "Results model · expected"
    ),
    statistics: t(
      "Statistikmodell · erwartet",
      "Statistics model · expected"
    ),
    market: t("Historische Marktrückrechnung", "Historical market inversion"),
    should: "Should Goals",
  };
  function comparison(id) {
    const games = selectedHistory(data.observations, id, lastSix ? recentCount : false);
    return (
      <>
        <div className="ta-controls">
          <ToggleButtonGroup
            exclusive
            size="small"
            value={lastSix ? "six" : "all"}
            onChange={(_, v) => v && setLastSix(v === "six")}
          >
            <ToggleButton value="all">{t("Saison", "Season")}</ToggleButton>
            <ToggleButton value="six">
              {t("Letzte Spiele", "Recent matches")}
            </ToggleButton>
          </ToggleButtonGroup>
          <Button onClick={() => setTotals(!totals)}>
            {totals
              ? t("Totals → Pro Spiel", "Totals → Per match")
              : t("Pro Spiel → Totals", "Per match → Totals")}
          </Button>
          <select
            aria-label={t("Heim/Auswärts", "Home/away")}
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
          >
            {[
              ["all", t("Gesamt", "Overall")],
              ["home", t("Heim", "Home")],
              ["away", t("Auswärts", "Away")],
            ].map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <select aria-label={t('Spielausgänge anzeigen als','Show outcomes as')} value={outcomeMode} onChange={e=>setOutcomeMode(e.target.value)}><option value="count">{t('S / R / N · Anzahl','W / D / L · Count')}</option><option value="probability">{t('Anteile / Wahrscheinlichkeiten','Frequencies / probabilities')}</option><option value="odds">{t('Faire Quoten','Fair odds')}</option></select>
        <label>{t("Letzte Spiele", "Recent matches")} <input aria-label={t("Anzahl Spiele", "Number of matches")} type="number" min="1" max="100" value={recentCount} onChange={e=>setRecentCount(Math.max(1,Math.min(100,Number(e.target.value)||8)))}/></label>
        <div className="ta-scroll ta-comparison-table">
          <table>
            <thead>
              <tr>
                {[
                  t("Grundlage", "Basis"),
                  t("Abdeckung", "Coverage"),
                  t("S / R / N", "W / D / L"),
                  t("Punkte / Spiel", "Points / game"),
                  t("Für", "For"),
                  t("Gegen", "Against"),
                  t("Differenz", "Difference"),
                  t("Quotient", "Ratio"),
                  t("Torschnitt", "Goals per match"),
                ].map((v) => (
                  <th key={v}>{v}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.keys(labelBasis).map((key) => {
                const s = historySummary(games, id, key, venue);
                return (
                  <React.Fragment key={key}>
                    <tr>
                      <td>
                        {labelBasis[key]}
                        {s.count<s.selected && <small className="ta-warning"> · {t("Unvollständige Grundlage", "Incomplete coverage")}</small>}
                        {key==='market' && games.some(f=>f.market && f.market.quality!=='verified') && <small className="ta-warning"> · {t("⚠ Teilweise ungeprüft / angenähert", "⚠ Partly unverified / approximate")}</small>}
                      </td>
                      <td>
                        {s.count}/{s.selected}
                      </td>
                      <td>{s.outcomes?['win','draw','loss'].map(k=>{
                        const v=s.outcomes[k],p=v/s.outcomes.count;
                        return outcomeMode==='count'?fmt(v,key==='real'?0:2):outcomeMode==='probability'?`${fmt(p*100)} %`:fmt(key!=='real'&&p>0?1/p:null);
                      }).join(' / '):'—'}</td>
                      <td>{fmt(s.outcomes?s.outcomes.points/s.outcomes.count:null)}</td>
                      <td>{fmt(totals ? s.for : s.for_average,totals && key==="real"?0:2)}</td>
                      <td>{fmt(totals ? s.against : s.against_average,totals && key==="real"?0:2)}</td>
                      <td>{fmt(s.count ? (totals?s.for:s.for_average)-(totals?s.against:s.against_average) : null,totals&&key==="real"?0:2)}</td>
                      <td>{fmt(s.quotient)}</td>
                      <td>{fmt(s.goal_sum)}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {matchList(id)}
        <small>
          {t(
            "Abdeckung bezieht sich auf dieselbe Spielauswahl. Fehlende Werte bleiben leer; historische Nachberechnungen sind keine damaligen Vorhersagen. Zukunftssettings stehen oben getrennt.",
            "Coverage refers to the same match selection. Missing values remain blank; historical backfills are not original predictions. Future settings are separate above."
          )}
        </small>
      </>
    );
  }
  function statisticDetails(id) {
    const team = stat?.teams?.[id],
      games = selectedHistory(data.observations, id, lastSix ? recentCount : false);
    if (!team)
      return (
        <p>
          {t(
            "Keine Statistikmodellwerte vorhanden.",
            "No statistics model values available."
          )}
        </p>
      );
    return (
      <>
        <div className="ta-guidance">
          <strong>{t("Anpassungsvorschläge", "Adjustment suggestions")}</strong>
          <p>
            {t(
              "Vorschläge berücksichtigen bisherige Erwartungen und die Anzahl vergleichbarer Spiele. Übernehmen ändert zunächst nur den Entwurf.",
              "Suggestions consider previous expectations and the number of comparable games. Applying them changes only the draft."
            )}
          </p>
          <details>
            <summary>
              {t("Wie wird vorgeschlagen?", "How are suggestions calculated?")}
            </summary>
            <p>
              {t(
                "Vergleich derselben Spiele: tatsächlicher / erwarteter Wert. Vorsichtig: n/(n+12), maximal 20 %; stärker: n/(n+6), maximal 40 %. Kleine Stichproben werden gedämpft. Regelbasierter Vorschlag, keine KI-Schätzung.",
                "Matched-game comparison: observed / expected. Cautious: n/(n+12), maximum 20%; stronger: n/(n+6), maximum 40%. Small samples are damped. Rule-based proposal, not an AI estimate."
              )}
            </p>
          </details>
          <Button disabled={busy} onClick={() => suggestions([id])}>
            {t("Team · vorsichtig vorschlagen", "Team · cautious proposal")}
          </Button>
          <Button disabled={busy} onClick={() => suggestions([id], true)}>
            {t("Team · stärker vorschlagen", "Team · stronger proposal")}
          </Button>
          <Button
            disabled={busy}
            onClick={() =>
              suggestions(stat.active_team_ids || Object.keys(stat.teams))
            }
          >
            {t("Liga · vorsichtig vorschlagen", "League · cautious proposal")}
          </Button>
        </div>
        {[
          ...Object.keys(team.statistics).map((key) => ({
            key,
            group: "statistic",
            values: team.statistics[key],
            name: i18n.t(
              "help.automation.business.statisticNames." +
                stat.registry[key]?.title,
              {
                defaultValue:
                  stat.registry[key]?.title || t("Statistik", "Statistic"),
              }
            ),
            weight: stat.weights[key],
          })),
          {
            key: "conversion",
            group: "conversion",
            values: team.conversion,
            name: "Conversion Rates",
          },
        ].map((item) => (
          <details key={item.key}>
            <summary>
              {item.name}{" "}
              {item.weight != null &&
                `· ${t("Gewicht", "Weight")} ${fmt(item.weight, 4)}`}
            </summary>
            <div className="ta-scroll">
              <table>
                <thead>
                  <tr>
                    <th>{t("Wert", "Value")}</th>
                    <th>{t("Zukunft · bearbeitbar", "Future · editable")}</th>
                    <th>{t("Historisch erwartet", "Historically expected")}</th>
                    <th>{t("Eingetreten", "Observed")}</th>
                    <th>{t("Spiele / Auswahl", "Games / selection")}</th>
                    <th>{t("Vorschlag", "Suggestion")}</th>
                    <th>
                      {t(
                        item.group === "conversion" ? "Zukunftstore gegen Ligareferenz · gesamt" : "Beitrag zu Zukunftstoren gegen Ligareferenz",
                        item.group === "conversion" ? "Future goals against league reference · total" : "Contribution to future goals against league reference"
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[...FOUR, "for_total", "against_total"].map((k) => {
                    const aggregate = k.endsWith("total"),
                      side = k === "for_total" ? "for" : "against",
                      value = aggregate
                        ? (item.values["home_" + side] +
                            item.values["away_" + side]) /
                          2
                        : item.values[k];
                    const percent =
                      item.group === "conversion" || Number(item.key) === 45;
                    const present = (v) =>
                      percent && Number.isFinite(v)
                        ? v > 1 && item.group !== "conversion"
                          ? v
                          : v * 100
                        : v;
                    const actual =
                        item.group === "statistic"
                          ? statisticObservation(games, id, item.key, k)
                          : conversionObservation(games, id, k),
                      expected =
                        item.group === "statistic"
                          ? statisticObservation(games, id, item.key, k, true)
                          : conversionObservation(games, id, k, true);
                    return (
                      <tr key={k}>
                        <td>{trField(k)}</td>
                        <td>
                          <ValueInput
                            value={percent ? value * 100 : value}
                            label={`${item.name} · ${trField(k)}${
                              percent ? " (%)" : ""
                            }`}
                            disabled={busy}
                            onChange={(v) =>
                              statEdit({
                                team_id: Number(id),
                                group: item.group,
                                statistic_id: item.key,
                                field: k,
                                value: percent ? v / 100 : v,
                              })
                            }
                          />
                        </td>
                        <td>
                          {fmt(present(expected?.mean), 3)}
                          {percent && expected?.mean != null ? " %" : ""}
                        </td>
                        <td>
                          {fmt(present(actual?.mean), 3)}
                          {percent && actual?.mean != null ? " %" : ""}
                        </td>
                        <td>
                          {actual
                            ? `${expected.count}/${expected.selected} · ${actual.count}/${actual.selected}`
                            : "—"}
                        </td>
                        <td>
                          {!aggregate
                            ? [false, true].map((stronger) => {
                                const proposal = statisticSuggestion(
                                  games,
                                  id,
                                  item.key,
                                  k,
                                  (item.group === "conversion" ? data.state.teams[id].conversion : data.state.teams[id].statistics[item.key])[k],
                                  stronger
                                );
                                return proposal ? (
                                  <Button
                                    key={String(stronger)}
                                    disabled={busy}
                                    title={t(
                                      "In den Entwurf übernehmen und Modellwirkung berechnen",
                                      "Apply to draft and calculate model impact"
                                    )}
                                    onClick={() =>
                                      statEdit({
                                        team_id: Number(id),
                                        group: item.group,
                                        statistic_id: item.key,
                                        field: k,
                                        value: proposal.value,
                                      })
                                    }
                                  >
                                    {stronger
                                      ? t("Stärker", "Stronger")
                                      : t("Vorsichtig", "Cautious")}{" "}
                                    {fmt(
                                      percent
                                        ? proposal.value * 100
                                        : proposal.value,
                                      3
                                    )}
                                    {percent ? " %" : ""}
                                  </Button>
                                ) : null;
                              })
                            : "—"}
                        </td>
                        <td>
                          {(() => {
                            const b = item.group === "conversion"
                              ? breakdown[id]?.statistics && Object.fromEntries(FOUR.map(field=>[field,Object.values(breakdown[id].statistics).every(row=>Number.isFinite(row[field])) ? Object.values(breakdown[id].statistics).reduce((sum,row)=>sum + row[field],0) : null]))
                              : breakdown[id]?.statistics?.[item.key];
                            return fmt(
                              b
                                ? aggregate && b
                                  ? (b["home_" + side] + b["away_" + side]) / 2
                                  : b?.[k]
                                : null,
                              4
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {item.group === "conversion" && <small>{t("Conversion = Tore / gewichtete Angriffsmomente. Historische Gesamtwerte: Summe Tore / Summe Angriffsmomente; Anzeige in %. Zukunftstore sind das Gesamtergebnis aller Statistikbeiträge, kein zusätzlicher Beitrag.", "Conversion = goals / weighted attack moments. Historical totals: sum of goals / sum of attack moments; shown as %. Future goals are the total of all statistic contributions, not an additional contribution.")}</small>}
            <small>
              {t(
                "Gesamtänderungen skalieren Heim und Auswärts proportional; Einzeländerungen erhalten die andere Spielstätte. Ballbesitz und Conversion werden in Prozent angezeigt.",
                "Overall edits proportionally scale home and away; individual edits preserve the other venue. Possession and conversion are displayed as percentages."
              )}
            </small>
          </details>
        ))}
      </>
    );
  }
  async function applyLeague() {
    if(!editing){setError(t("Zum Ändern zuerst Bearbeiten wählen.","Choose Edit before changing settings."));return;}
    const value = Number(target);
    if (!(value > 0)) return;
    setPreview(null);
    if (leagueModel === "statistics")
      return statEdit({ group: "league", field: leagueAction, value });
    try {
      const rows = Object.values(draft).map(futureSummary);
      if (rows.some((x) => !x)) throw Error("INCOMPLETE_LEAGUE_SETTINGS");
      setDraft(
        Object.fromEntries(
          {
            goals: leagueAction === "set" ? setGoalAverage : adjustGoalAverage,
            home:
              leagueAction === "set" ? setHomeAdvantage : adjustHomeAdvantage,
            draw: leagueAction === "set" ? setDrawFactor : adjustDrawFactor,
          }
            [leagueMetric](rows, value)
            .map((r) => [r.team_id, r])
        )
      );
    } catch (e) {
      setError(e.message);
    }
  }
  function normalizeDraft(kind) {
    if (!workspace || !editing || busy) return;
    if (leagueModel === "statistics") return statEdit({group:"league",field:"normalize",value:1});
    try {
      const rows=Object.values(draft).map(futureSummary);
      const next=(kind==="normalize-goals"?normalizeGoalTotals:normalizeHomeAdvantage)(rows);
      setDraft(Object.fromEntries(next.map(r=>[r.team_id,r])));setPreview(null);setSaved(false);
    } catch(e) {setError(e.message);}
  }
  const leagueAverage = average(
    (leagueModel === "result"
      ? Object.values(draft).map(futureSummary)
      : rank
    ).filter(Boolean),
    leagueModel === "statistics"
      ? "total_goals"
      : { goals: "total_goals", home: "for_homefactor", draw: "draw_factor" }[
          leagueMetric
        ]
  );
  return (
    <ThemeProvider theme={createWorkareaTheme(mode)}><EditingContext.Provider value={editing}>
      <Dialog
        open
        fullScreen
        onClose={() => (dirty ? setConfirmClose(true) : onClose())}
        PaperProps={{ className: `team-analysis-legacy ta-${mode}` }}
      >
        <DialogTitle>
          <div className="ta-show-options"><Button onClick={()=>{setShortShow(true);setTour(-1);}}>{t("Kurze Show", "Short show")}</Button><Button onClick={()=>{setShortShow(false);setTour(-1);}}>{t("Lange Show", "Long show")}</Button></div>
          <div className="ta-heading">
            {workspace&&<Button disabled={editing || data?.read_only} onClick={()=>setEditing(true)}>{editing?t("Bearbeitung aktiv","Editing enabled"):t("Persönliche Settings bearbeiten","Edit personal settings")}</Button>}
            <strong>
              {teamIds.map(id=><React.Fragment key={id}>{logo(id)}</React.Fragment>)}
              {focus ? names(focus) : teamIds.map(names).join(" – ")} ·{" "}
              {t("Team Settings & Analyse", "Team settings & analysis")}
            </strong>
            <Button
              onClick={() => {
                const m = mode === "dark" ? "light" : "dark";
                setMode(m);
                saveWorkareaMode(m);
              }}
            >
              {mode === "dark" ? t("Hell", "Light") : t("Dunkel", "Dark")}
            </Button>
            <Button
              onClick={() => {
                if (tour < 0) {setVenue("all");setTotals(true);setLastSix(false);}
                if (tour < 0 && teamIds.length > 1) setFocus(activeIds[0]);
                setTour(tour < 0 ? 0 : -1);
              }}
            >
              {tour < 0
                ? t("Geführte Rundreise", "Guided tour")
                : t("Rundreise beenden", "End tour")}
            </Button>
            {workspace&&activeIds.length===1&&<Button onClick={()=>setExplanationIntent(v=>({count:v.count+1}))}>{t("Erzähl mir vom Team","Tell me about this team")}</Button>}
            {onStart&&<Button onClick={onStart}>{t("Start","Start")}</Button>}
            <Button onClick={() => (dirty ? setConfirmClose(true) : onClose())}>
              {workspace?t("Zur Liga", "Back to league"):t("Schließen", "Close")}
            </Button>
          </div>
          <small>
            {league.name} · {data?.season?.name || ""}
          </small>
          {data&&<nav className="ta-team-navigation" aria-label={t("Mannschaften und Bereiche","Teams and sections")}>
            {teamIds.map(id=><a key={id} href={`#team-settings-${id}`} onClick={event=>{event.preventDefault();setFocus(null);setTimeout(()=>document.getElementById(`team-settings-${id}`)?.scrollIntoView({block:"start",behavior:"smooth"}),0);}}>{names(id)} · {t("Modellwerte","Model settings")}</a>)}
            {activeIds.map(id=><a key={id} href={`#team-evidence-${id}`} onClick={event=>{event.preventDefault();setTimeout(()=>document.getElementById(`team-evidence-${id}`)?.scrollIntoView({block:"start",behavior:"smooth"}),0);}}>{names(id)} · {t("Bilanz & Quellen","Record & sources")}</a>)}
            {fixture&&<a href="#team-pair-market" onClick={event=>{event.preventDefault();setTimeout(()=>document.getElementById("team-pair-market")?.scrollIntoView({block:"start",behavior:"smooth"}),0);}}>{t("Paarung & Markt","Fixture & market")}</a>}
          </nav>}
        </DialogTitle>
        <DialogContent className="ta-analysis-content">
        {data&&<div className={"ta-fixed-settings"+highlight("future")} data-tour="future" onToggle={e=>{const d=e.target;if(d.open&&d.matches?.(".ta-all-editors,.ta-model-details"))e.currentTarget.querySelectorAll(".ta-all-editors[open],.ta-model-details[open]").forEach(other=>{if(other!==d&&!other.contains(d))other.open=false;});}}><div className="ta-settings-label">✎ {t("Team Settings · Hier bearbeiten","Team settings · Edit here")}</div><div className="ta-review-controls" aria-label={t("Gespeicherte Zukunftssettings und Entwurf","Stored future settings and draft")}>
          <label>{t("Was prüfen?","Review")}: <select aria-label={t("Kennzahl prüfen","Review metric")} value={decisionMetric} onChange={e=>setDecisionMetric(e.target.value)}><option value="total_strength">{t("Spielstärke prüfen","Review strength")}</option><option value="total_goals">{t("Torniveau prüfen","Review goal level")}</option></select></label>
          <label>{t("Modell auswählen","Select model")}: <select aria-label={t("Modell auswählen","Select model")} value={decisionModel} onChange={e=>setDecisionModel(e.target.value)}><option value="result">{t("Ergebnismodell","Results model")}</option><option value="statistics">{t("Statistikmodell","Statistics model")}</option></select></label>
          <label>{t("Ebene","Level")}: <select aria-label={t("Bearbeitungsebene","Editing level")} value={decisionLevel} onChange={e=>selectDecisionLevel(e.target.value)}><option value="summary">{t("1 · Gesamtspielstärke","1 · Overall strength")}</option><option value="composition">{t("2 · Für / Gegen","2 · For / Against")}</option><option value="venue">{t("3 · Heim / Auswärts","3 · Home / Away")}</option></select></label>
          {decisionLevel!=="summary"&&<label>{t("Seite","Side")}: <select aria-label={t("Bearbeitungsseite","Editing side")} value={decisionSide} onChange={e=>setDecisionSide(e.target.value)}><option value="for">{t("Für","For")}</option><option value="against">{t("Gegen","Against")}</option></select></label>}
          <label>{t("Spielort","Venue")}: <select disabled={decisionLevel==="composition"} aria-label={t("Vergleichsspielort","Comparison venue")} value={venue} onChange={e=>setVenue(e.target.value)}>{decisionLevel!=="venue"&&<option value="all">{t("Gesamt","All")}</option>}<option value="home">{t("Heim","Home")}</option><option value="away">{t("Auswärts","Away")}</option></select></label>
          <label>{t("Jüngste Spiele","Recent matches")}: <select aria-label={t("Jüngste Spiele","Recent matches")} value={recentCount} onChange={e=>setRecentCount(Number(e.target.value))}><option value={6}>6</option><option value={8}>8</option></select></label>
          {!editing&&<small>{data.read_only?t("Dieser Datenbestand ist schreibgeschützt.","These data are read-only."):t("Zum Ändern oben Persönliche Settings bearbeiten wählen.","Choose Edit personal settings above to make changes.")}</small>}
        </div>{activeIds.map(id=><section className="ta-team-settings" id={`team-settings-${id}`} key={id} aria-label={`${names(id)} · ${t("Settings beider Modelle","Both model settings")}`}>
          <TeamReviewIdentity id={id} name={names(id)} logo={logo(id)} fixture={fixture} en={en}/>
          <TeamReviewEvidence data={data} teamId={id} name={names(id)} en={en} venue={venue}/>
          <div className="ta-models">{summaryCard(id,"result")}{summaryCard(id,"statistics")}</div><details className="ta-all-editors"><summary>{names(id)} · {t("Weitere Teamwerte bearbeiten","Edit additional team values")}</summary>                        <section
                          data-tour="details"
                          className={"ta-section" + highlight("details")}
                        >
                          <details>
                            <summary>
                              {t(
                                "Ergebnismodell · vollständige Einzelwerte",
                                "Results model · all individual values"
                              )}
                            </summary>
                            <div className="ta-input-grid">
                              {[
                                "for_home",
                                "against_home",
                                "for_away",
                                "against_away",
                                "for_total",
                                "against_total",
                                "draw_factor",
                              ].map((k) => (
                                <ValueInput
                                  key={k}
                                  label={trField(k)}
                                  value={futureSummary(draft[id])?.[k]}
                                  disabled={busy}
                                  onChange={(v) => edit(id, k, v)}
                                />
                              ))}
                            </div>
                          </details>
                        </section>
                        <section
                          data-tour="statistics"
                          className={"ta-section" + highlight("statistics")}
                        >
                          <h3>
                            {t(
                              "Einzelstatistiken · Statistikmodell",
                              "Individual statistics · statistics model"
                            )}
                          </h3>
                          {statisticDetails(id)}
                        </section>
</details></section>)}<details className="ta-all-editors ta-league-editors"><summary>{t("Ligaweite Parameter & Einstellungen","League-wide parameters & settings")}</summary>                  {workspace && <TeamModelReferences data={{...data,result_teams:draft}} state={stat} ranking={rank} teamIds={activeIds} en={en}/>}
                  <section
                    data-tour="season"
                    className={"ta-section" + highlight("season")}
                  >
                    <h3>
                      {t(
                        "Season Parameters · gesamte Liga",
                        "Season parameters · entire league"
                      )}
                    </h3>
                    <p>
                      {t(
                        "Gespeicherter Ergebnismodell-Torschnitt",
                        "Stored results-model goal average"
                      )}
                      : {fmt(data.result_parameters?.stored_goal_average, 4)} ·{" "}
                      {t("Heimfaktor", "Home factor")}:{" "}
                      {fmt(data.result_parameters?.exp_home_factor, 4)} ·{" "}
                      {t("Remisfaktor", "Draw factor")}:{" "}
                      {fmt(data.result_parameters?.drawfactor, 4)}
                    </p>
                    <p>
                      {t("Reale Ligabilanz", "Actual league record")}:{" "}
                      {data.league_actual?.games ?? "—"} {t("Spiele", "games")}{" "}
                      · {t("Torschnitt", "Goals per match")}{" "}
                      {fmt(
                        data.league_actual?.games
                          ? (data.league_actual.home +
                              data.league_actual.away) /
                              data.league_actual.games
                          : null,
                        4
                      )}{" "}
                      · {t("Heimvorteil", "Home advantage")}{" "}
                      {fmt(
                        data.league_actual?.home + data.league_actual?.away > 0
                          ? data.league_actual.home /
                              ((data.league_actual.home +
                                data.league_actual.away) /
                                2)
                          : null,
                        4
                      )}
                    </p>
                    <p>
                      {t(
                        "Statistikmodell · Durchschnitt der aktuellen Teamsettings",
                        "Statistics model · current team settings average"
                      )}
                      : {fmt(average(rank, "total_goals"), 4)} ·{" "}
                      {t(
                        "Offensiver / defensiver Heimfaktor",
                        "Attacking / defensive home factor"
                      )}
                      :{" "}
                      {fmt(
                        average(
                          rank.map(futureSummary).filter(Boolean),
                          "for_homefactor"
                        ),
                        4
                      )}{" "}
                      /{" "}
                      {fmt(
                        average(
                          rank.map(futureSummary).filter(Boolean),
                          "against_homefactor"
                        ),
                        4
                      )}
                    </p>
                    <p>
                      {t(
                        "Statistikmodell · gespeicherte Updatefaktoren",
                        "Statistics model · stored update factors"
                      )}
                      : {fmt(stat?.parameters?.statistics_update_factor, 5)} /{" "}
                      {fmt(stat?.parameters?.conversion_update_factor, 5)}
                    </p>

                    <div className="ta-controls">
                      {workspace && <Button disabled={!editing || busy} onClick={()=>normalizeDraft("normalize-goals")}>{leagueModel === "statistics" ? t("Statistikwerte normieren · Entwurf","Normalize statistics · draft") : t("Torwerte normieren · Entwurf","Normalize goal values · draft")}</Button>}
                      {workspace && leagueModel === "result" && <Button disabled={!editing || busy} onClick={()=>normalizeDraft("normalize-home")}>{t("Heimvorteil normieren · Entwurf","Normalize home advantage · draft")}</Button>}
                      <select
                        aria-label={t(
                          "Modell für Liga-Anpassung",
                          "Model for league adjustment"
                        )}
                        value={leagueModel}
                        onChange={(e) => {
                          setLeagueModel(e.target.value);
                          setLeagueMetric("goals");
                        }}
                      >
                        <option value="result">
                          {t("Ergebnismodell", "Results model")}
                        </option>
                        <option value="statistics">
                          {t("Statistikmodell", "Statistics model")}
                        </option>
                      </select>
                      {leagueModel === "result" && (
                        <select
                          aria-label={t("Liga-Kennzahl", "League metric")}
                          value={leagueMetric}
                          onChange={(e) => {
                            setLeagueMetric(e.target.value);
                            setTarget(
                              e.target.value === "goals"
                                ? "3.1"
                                : e.target.value === "home"
                                ? "1.1"
                                : "1"
                            );
                          }}
                        >
                          <option value="goals">
                            {t("Torschnitt", "Goals per match")}
                          </option>
                          <option value="home">
                            {t("Heimvorteil", "Home advantage")}
                          </option>
                          <option value="draw">
                            {t("Remisfaktor", "Draw factor")}
                          </option>
                        </select>
                      )}
                      <select
                        aria-label={t("Liga-Anpassung", "League adjustment")}
                        value={leagueAction}
                        onChange={(e) => setLeagueAction(e.target.value)}
                      >
                        <option value="scale">
                          {t(
                            "Individuelle Werte proportional anpassen",
                            "Scale individual team values proportionally"
                          )}
                        </option>
                        <option value="set">
                          {t(
                            "Gleicher Zielwert für alle Teams",
                            "Same target for every team"
                          )}
                        </option>
                      </select>
                      <TextField
                        size="small"
                        label={t(
                          "Ziel-Ligadurchschnitt",
                          "Target league average"
                        )}
                        type="number"
                        value={target}
                        onChange={(e) => setTarget(e.target.value)}
                      />
                      <Button disabled={busy} onClick={applyLeague}>
                        {t(
                          "Gesamte Liga in Vorschau übernehmen",
                          "Apply entire league to draft"
                        )}
                      </Button>
                    </div>
                    <p>
                      {t(
                        "Aktueller Entwurfsdurchschnitt",
                        "Current draft average"
                      )}{" "}
                      {fmt(leagueAverage, 4)} → {target} ·{" "}
                      {t("Faktor", "Factor")}{" "}
                      {leagueAction === "scale" && leagueAverage > 0
                        ? fmt(Number(target) / leagueAverage, 5)
                        : t("individuell je Team", "individual per team")}
                    </p>
                  </section>
                  {leagueModel === "result" && leagueMetric === "home" && (
                    <p>
                      {t(
                        "Die bestehende globale Heim-Anpassung setzt den offensiven Zielmittelwert und den defensiven Zielmittelwert auf 2 minus Ziel. Individuelle Faktoren werden unabhängig angezeigt und bearbeitet.",
                        "The existing global home adjustment targets the attacking mean and a defensive mean of 2 minus the target. Individual factors are displayed and edited independently."
                      )}
                    </p>
                  )}
</details></div>}

          {data?.preview_notice && <Alert severity="info">{data.preview_notice[en?"en":"de"]}</Alert>}
          {error && (
            <Alert severity="error">
              {t(
                "Aktion nicht abgeschlossen. Entwurf erhalten.",
                "Action not completed. Draft retained."
              )}{" "}
              <details>
                <summary>
                  {t("Technische Details", "Technical details")}
                </summary>
                {error}
              </details>
            </Alert>
          )}
          {saved && (
            <Alert severity="success">
              {t("Gespeichert und erneut gelesen.", "Saved and reloaded.")}
            </Alert>
          )}
          {busy && (
            <div role="status">
              <CircularProgress size={20} />{" "}
              {t("Daten werden verarbeitet …", "Processing data …")}
            </div>
          )}
          {data && (
            <>
              {fixture && (
                <PairIndication
                  fixture={fixture}
                  data={data}
                  threshold={indicationThreshold}
                  en={en}
                />
              )}
              <div className={"ta-layout" + (tour >= 0 ? " touring" : "")}>
                <main>
                  {focus && (
                    <Button onClick={() => setFocus(null)}>
                      {t("Zurück zur Paarung", "Back to both teams")}
                    </Button>
                  )}
                  {activeIds.map((id) => {
                    const { actual, real } = actualBalance(id);
                    return (
                      <section className="ta-team" id={`team-evidence-${id}`} key={id}>
                        <TeamReviewIdentity id={id} name={names(id)} logo={logo(id)} fixture={fixture} en={en}/>
                        <details className="ta-balance-details" open={!workspace||undefined}><summary>{names(id)} · {t("Bilanz, Vorsaison & Ligatabelle","Record, previous season & league table")}</summary><header
                          data-tour="balance"
                          className={"ta-balance" + highlight("balance")}
                        >
                          <h2 onDoubleClick={()=>setFocus(id)}>{logo(id)}{names(id)} {data.transitions?.some(x=>x.team_id===Number(id)&&x.transition_type==="promoted") && <span title={t("Aufsteiger","Promoted")}>↑</span>}</h2>
                          {teamIds.length > 1 && !focus && (
                            <Button
                              onClick={() => {
                                setFocus(id);
                                setTour(-1);
                              }}
                            >
                              {t("Mannschaft vertiefen", "Explore this team")}
                            </Button>
                          )}
                          <div className="ta-record-line">{t("S–R–N", "W–D–L")}: {fmt(actual?.won,0)}–{fmt(actual?.draw,0)}–{fmt(actual?.lost,0)} · {t("Punkte pro Spiel", "Points per game")}: {fmt(actual?.played>0?actual.points/actual.played:null)}</div>
                          <div className="ta-balance-numbers">
                            <span>
                              {t("Platz", "Position")}{" "}
                              <b>{fmt(actual?.position, 0)}</b>
                            </span>
                            <span>
                              {t("Spiele", "Games")}{" "}
                              <b>{fmt(actual?.played ?? real.count, 0)}</b>
                            </span>
                            <span>
                              {t("Punkte", "Points")}{" "}
                              <b>{fmt(actual?.points, 0)}</b>
                            </span>
                            <span>
                              {t("Tore", "Goals")}{" "}
                              <b>
                                {fmt(actual?.goalsScored ?? real.for, 0)}−
                                {fmt(actual?.goalsAgainst ?? real.against, 0)}
                              </b>
                            </span>
                          </div>
                          <p>
                            {t("Pro Spiel", "Per match")}:{" "}
                            {fmt(real.for_average)} :{" "}
                            {fmt(real.against_average)} ·{" "}
                            {t("Quotient", "Ratio")} {fmt(real.quotient)} ·{" "}
                            {t("Torschnitt", "Goals per match")}{" "}
                            {fmt(real.goal_sum)}
                          </p>
                          {previousSummary(id)}
                          <details onToggle={e=>{if(e.currentTarget.open)e.currentTarget.querySelector(".tl-selected")?.scrollIntoView?.({block:"nearest"})}}><summary>{t("Ligatabelle öffnen", "Open league table")}</summary><TeamLeagueInline data={data} teamId={id} en={en} mode={mode}/></details>
                        </header></details>
                        {workspace&&activeIds.length===1&&<TeamDecisionReview data={data} teamId={id} model={decisionModel} metric={decisionMetric} level={decisionLevel} side={decisionSide} onLevel={selectDecisionLevel} venue={venue} recent={[6,8].includes(recentCount)?recentCount:8} en={en} intent={explanationIntent}/>}
                        <details className="ta-all-numbers"><summary>{names(id)} · {t("Quellen, letzte Spiele und Berechnung","Sources, recent matches and calculation")}</summary>
                        <section
                          data-tour="comparison"
                          className={"ta-section" + highlight("comparison")}
                        >
                          <h3>
                            {t(
                              "Settings und Leistung vergleichen",
                              "Compare settings and performance"
                            )}
                          </h3>
                          {workspace&&<TeamSourceComparison key={tour>=0?stepIndices[tour]:"manual"} observations={data.observations} teamId={id} en={en} data={data} locked={tour>=0}/>}
                          {comparison(id)}
                        </section>
                        <section
                          data-tour="previous"
                          className={"ta-section" + highlight("previous")}
                        >
                          <h3>
                            {t(
                              "Vorsaison · gespeicherte Quellen",
                              "Previous season · stored sources"
                            )}
                          </h3>
                          {data.history?.teams
                            ?.find((r) => r.team_id === Number(id))
                            ?.sources?.map((s) => (
                              <p key={s.season_id}>
                                {s.league_name} · {s.season_name} · {s.played}{" "}
                                {t("Spiele", "games")} ·{" "}
                                {t("Realer Quotient", "Actual ratio")}{" "}
                                {fmt(
                                  s.actual_totals?.against > 0
                                    ? s.actual_totals.for /
                                        s.actual_totals.against
                                    : null
                                )}{" "}
                                · {t("Torschnitt", "Goals per match")}{" "}
                                {fmt(
                                  s.actual_totals?.played > 0
                                    ? (s.actual_totals.for +
                                        s.actual_totals.against) /
                                        s.actual_totals.played
                                    : null
                                )}{" "}
                                ·{" "}
                                {t(
                                  "Historische Settings",
                                  "Historical settings"
                                )}
                                : {fmt(s.settings?.total_strength)} /{" "}
                                {fmt(s.settings?.total_goals)} ·{" "}
                                {s.settings_source === "own"
                                  ? t(
                                      "eigene historische Settings",
                                      "own historical settings"
                                    )
                                  : s.settings_source === "operator_reference"
                                  ? t(
                                      "Betreiber-Referenz",
                                      "operator reference"
                                    )
                                  : t("Settings fehlen", "settings missing")}
                              </p>
                            ))}
                          {!data.history?.teams?.find(
                            (r) => r.team_id === Number(id)
                          )?.sources?.length && (
                            <p>
                              {t(
                                "Keine passende Vorsaisonquelle vorhanden.",
                                "No matching previous-season source available."
                              )}
                            </p>
                          )}
                        </section>
                        </details>
                      </section>
                    );
                  })}
                  {preview && (
                    <section
                      className="ta-section"
                      aria-label={t("Vorschau", "Preview")}
                    >
                      <h3>
                        {t(
                          "Vorschau · noch nicht gespeichert",
                          "Preview · not saved"
                        )}
                      </h3>
                      {[
                        ["result", t("Ergebnismodell", "Results model")],
                        [
                          "statistics",
                          t("Statistikmodell", "Statistics model"),
                        ],
                      ].map(([key, label]) => (
                        <div key={key}>
                          <h4>{label}</h4>
                          {preview[key].map((r) => (
                            <details key={r.id} open>
                              <summary>{names(r.id)}</summary>
                              {r.changes.map((c) => (
                                <p key={c.field}>
                                  {trField(c.field)}: {fmt(c.before, 5)} →{" "}
                                  <strong>{fmt(c.after, 5)}</strong>
                                </p>
                              ))}
                            </details>
                          ))}
                        </div>
                      ))}
                      <h4>
                        {t(
                          "Statistikmodell · Einzelwertänderungen",
                          "Statistics model · individual changes"
                        )}
                      </h4>
                      {preview.statistic_fields.map((c, i) => (
                        <p key={i}>
                          {names(c.id)} ·{" "}
                          {c.group === "conversion"
                            ? "Conversion"
                            : i18n.t(
                                "help.automation.business.statisticNames." +
                                  stat.registry[c.group]?.title,
                                {
                                  defaultValue:
                                    stat.registry[c.group]?.title ||
                                    t("Statistik", "Statistic"),
                                }
                              )}{" "}
                          · {trField(c.field)}: {fmt(c.before, 5)} →{" "}
                          {fmt(c.after, 5)}
                        </p>
                      ))}
                      <h4>Season Parameters</h4>
                      {changedFields(
                        data.result_parameters,
                        preview.parameters
                      ).map((c) => (
                        <p key={c.field}>
                          {trField(c.field)}: {fmt(c.before, 5)} →{" "}
                          {fmt(c.after, 5)}
                        </p>
                      ))}
                      <p>
                        {t(
                          "Historische Beobachtungen und gespeicherte Pre-Match-Prognosen bleiben erhalten. Neue Settings werden durch die bestehenden Prognosewege verwendet.",
                          "Historical observations and stored pre-match forecasts are retained. Existing prediction paths use the new settings."
                        )}
                      </p>
                    </section>
                  )}
                </main>
                {tour >= 0 && (
                  <aside className="ta-tour">
                    <h2>{t("Rundreise", "Guided tour")}</h2>
                    <small>
                      {tour + 1}/{steps.length}
                    </small>
                    <h3>{steps[tour][1]}</h3>
                    <p>{names(activeIds[0])}</p>
                    <p>{tourExplanation(activeIds[0])}</p>
                    <ol>
                      {steps.map((s, i) => (
                        <li key={i}>
                          <Button
                            onClick={() => setTour(i)}
                            aria-current={i === tour ? "step" : undefined}
                          >
                            {s[1]}
                          </Button>
                        </li>
                      ))}
                    </ol>
                    <Button
                      disabled={tour === 0}
                      onClick={() => setTour(tour - 1)}
                    >
                      {t("Zurück", "Back")}
                    </Button>
                    <Button
                      onClick={() =>
                        setTour(tour === steps.length - 1 ? -1 : tour + 1)
                      }
                    >
                      {t("Weiter", "Next")}
                    </Button>
                    <Button
                      onClick={() =>
                        setTour(tour === steps.length - 1 ? -1 : tour + 1)
                      }
                    >
                      {t("Überspringen", "Skip")}
                    </Button>
                    <Button onClick={() => setTour(-1)}>
                      {t("Beenden", "End")}
                    </Button>
                  </aside>
                )}
              </div>
            </>
          )}
        </DialogContent>
        {data && dirty && (
          <div className="ta-live-impact" aria-live="polite">
            {activeIds.map((id) => {
              const before = data.ranking?.find(
                  (r) => r.team_id === Number(id)
                ),
                after = rank.find((r) => r.team_id === Number(id));
              return (
                <span key={id}>
                  <strong>{names(id)}</strong>
                  {" · "}
                  {t("Statistik", "Statistics")}:{" "}
                  {fmt(before?.total_strength, 3)} →{" "}
                  {fmt(after?.total_strength, 3)}
                  {" · "}
                  {t("Torschnitt", "Goals")}: {fmt(before?.total_goals, 3)} →{" "}
                  {fmt(after?.total_goals, 3)}
                  {" · "}
                  {t("Rang", "Rank")}: {before?.rank ?? "—"} →{" "}
                  {after?.rank ?? "—"}
                  {" · "}
                  {t("Ergebnisquotient", "Results ratio")}:{" "}
                  {fmt(futureSummary(draft[id])?.total_strength, 3)}
                </span>
              );
            })}
            <details>
              <summary>
                {t("Liga-Rangänderungen", "League rank changes")}
              </summary>
              <div className="ta-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>{t("Team", "Team")}</th>
                      <th>
                        {t("Quotient vorher → nachher", "Ratio before → after")}
                      </th>
                      <th>
                        {t(
                          "Torschnitt vorher → nachher",
                          "Goals before → after"
                        )}
                      </th>
                      <th>
                        {t("Rang vorher → nachher", "Rank before → after")}
                      </th>
                      <th>{t("Ergebnismodell", "Results model")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rank.map((r) => {
                      const b = data.ranking.find(
                        (x) => x.team_id === r.team_id
                      );
                      return (
                        <tr key={r.team_id}>
                          <td>{names(r.team_id)}</td>
                          <td>
                            {fmt(b?.total_strength, 3)} →{" "}
                            {fmt(r.total_strength, 3)}
                          </td>
                          <td>
                            {fmt(b?.total_goals, 3)} → {fmt(r.total_goals, 3)}
                          </td>
                          <td>
                            {b?.rank} → {r.rank}
                          </td>
                          <td>
                            {fmt(
                              futureSummary(draft[r.team_id])?.total_strength,
                              3
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        )}
        <DialogActions>
          <span>
            {dirty
              ? t("Ungespeicherte Änderungen", "Unsaved changes")
              : t("Keine ungespeicherten Änderungen", "No unsaved changes")}
          </span>
          <Button
            disabled={busy || !dirty}
            onClick={() => {
              setDraft(data.result_teams);
              setStat(data.state);
              setBreakdown(data.breakdown || {});
              setRank(data.ranking);
              setEdits([]);
              setPreview(null);
              setError("");
            }}
          >
            {t("Verwerfen", "Discard")}
          </Button>
          <Button disabled={busy || !dirty} onClick={showPreview}>
            {t("Vorschau", "Preview")}
          </Button>
          <Button
            variant="contained"
            disabled={busy || !dirty || !preview}
            onClick={save}
          >
            {t("Änderungen speichern", "Save changes")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog sx={{zIndex:1600}} open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>
          {t(
            "Ungespeicherte Änderungen verwerfen?",
            "Discard unsaved changes?"
          )}
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>
            {t("Weiter bearbeiten", "Continue editing")}
          </Button>
          <Button onClick={onClose}>
            {t("Verwerfen und schließen", "Discard and close")}
          </Button>
        </DialogActions>
      </Dialog>
    </EditingContext.Provider></ThemeProvider>
  );
}
function PairIndication({ fixture, data, threshold, en }) {
  const [market, setMarket] = useState("over-under"),
    [line, setLine] = useState("2.50"),
    [odds, setOdds] = useState(null),
    [picked, setPicked] = useState(0),
    [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setOdds(null);
    setPicked(0);
    setError("");
    fetchFixtureMarketOdds(fixture.id, market, { line })
      .then((r) => active && setOdds(r))
      .catch(
        () =>
          active &&
          setError(
            en ? "Market data unavailable" : "Marktdaten nicht verfügbar"
          )
      );
    return () => {
      active = false;
    };
  }, [fixture.id, market, line, en]);
  const observation = data.observations.find(
      (f) => f.id === Number(fixture.id)
    ),
    outcomes =
      market === "1x2"
        ? ["1", "X", "2"]
        : market === "asian-handicap"
        ? ["home", "away"]
        : market === "btts"
        ? ["yes", "no"]
        : ["over", "under"];
  const fair = (model) =>
    market === "1x2"
      ? ["homewinprob", "drawprob", "awaywinprob"].map(
          (k) =>
            observation?.[model]?.fair_odds?.[k] ??
            (observation?.[model]?.probabilities?.[k] > 0
              ? 1 / observation[model].probabilities[k]
              : null)
        )
      : getLeagueFairMarketValues(
          model === "result"
            ? odds?.prematch_context?.historical
              ? odds.prematch_context.forecast
              : {
                  ...fixture,
                  fair_markets:
                    observation?.result?.fair_markets || fixture.fair_markets,
                }
            : observation?.statistic_forecast,
          market,
          { goalLine: line, handicapLine: line, raw: true }
        );
  const models = {
      result: fair("result"),
      statistic: fair("statistics_model"),
    },
    quotes = {
      highest: outcomes.map((k) => odds?.outcomes?.[k]?.highest),
      average: outcomes.map((k) => odds?.outcomes?.[k]?.average),
    },
    ind = workspaceIndications({
      row: fixture,
      outcomes,
      models,
      quotes,
      threshold,
      prematchContext: odds?.prematch_context,
      market,
      line: ["over-under", "asian-handicap"].includes(market) ? line : null,
      loading: !odds && !error,
    });
  const bookNames = (m) => {
    if (m.basis === "average")
      return en ? "Average quote" : "Durchschnittsquote";
    const groups = new Map();
    for (const r of odds?.prematch_context?.records || []) {
      const id = r.bookmaker_id ?? r.bookmaker?.id;
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(r);
    }
    const names = [...groups.values()]
      .filter(
        (rs) =>
          aggregateFixtureMarketOdds(rs, market, { line }).outcomes?.[m.side]
            ?.highest === m.quote
      )
      .map((rs) => rs[0].bookmaker?.name || rs[0].bookmaker_name)
      .filter(Boolean);
    return (
      (en ? "Best quote" : "Bestquote") +
      " · " +
      (names.length
        ? [...new Set(names)].join(", ")
        : en
        ? "provider name unavailable"
        : "Anbietername nicht verfügbar")
    );
  };
  return (
    <section id="team-pair-market" className="ta-indication">
      <p className="ta-source-note">{en ? "Stored match forecasts and current market quotes. These are separate from the unsaved settings draft; saved changes affect forecasts after recompilation." : "Gespeicherte Spielprognosen und vorhandene Marktquoten. Diese sind vom ungespeicherten Settings-Entwurf getrennt; gespeicherte Änderungen wirken nach erneutem Kompilieren auf die Prognosen."}</p>
      <div className="ta-controls">
        <strong>
          {fixture.localTeam?.custom_name || fixture.localTeam?.name || data.observations.find(f=>f.home_team_id===Number(fixture.localTeam?.id ?? fixture.home_team_id))?.home_name || (en?"Home team":"Heimmannschaft")} – {fixture.visitorTeam?.custom_name || fixture.visitorTeam?.name || data.observations.find(f=>f.away_team_id===Number(fixture.visitorTeam?.id ?? fixture.away_team_id))?.away_name || (en?"Away team":"Auswärtsmannschaft")}
          {" · "}{en ? "Pre-match market comparison" : "Pre-Match-Marktvergleich"}
        </strong>
        <select
          aria-label={en ? "Market" : "Markt"}
          value={market}
          onChange={(e) => setMarket(e.target.value)}
        >
          {["1x2", "over-under", "asian-handicap", "btts"].map((k) => (
            <option key={k} value={k}>
              {
                {
                  "1x2": "1 / X / 2",
                  "over-under": en ? "Over / Under" : "Über / Unter",
                  "asian-handicap": "Asian Handicap",
                  btts: en ? "Both teams score" : "Beide Teams treffen",
                }[k]
              }
            </option>
          ))}
        </select>
        {["over-under", "asian-handicap"].includes(market) && (
          <TextField
            size="small"
            label="Line"
            type="number"
            inputProps={{ step: 0.25 }}
            value={line}
            onChange={(e) => setLine(Number(e.target.value).toFixed(2))}
          />
        )}
      </div>
      {ind.matches.length > 1 && (
        <select
          aria-label={en ? "Selected indication" : "Ausgewählte Indikation"}
          value={Math.min(picked, ind.matches.length - 1)}
          onChange={(e) => setPicked(Number(e.target.value))}
        >
          {ind.matches.map((m, i) => (
            <option key={i} value={i}>
              {m.side.toUpperCase()} ·{" "}
              {m.model === "result"
                ? en
                  ? "Results model"
                  : "Ergebnismodell"
                : en
                ? "Statistics model"
                : "Statistikmodell"}{" "}
              ·{" "}
              {m.basis === "average"
                ? en
                  ? "Average"
                  : "Durchschnitt"
                : en
                ? "Best quote"
                : "Bestquote"}
            </option>
          ))}
        </select>
      )}
      {ind.matches.length ? (
        [ind.matches[Math.min(picked, ind.matches.length - 1)]].map((m, i) => (
          <p key={i}>
            <strong>
              {m.side.toUpperCase()}{" "}
              {["over-under", "asian-handicap"].includes(market)
                ? Number(line).toLocaleString(en ? "en-GB" : "de-DE")
                : ""}
            </strong>{" "}
            ·{" "}
            {m.model === "result"
              ? en
                ? "Results model"
                : "Ergebnismodell"
              : en
              ? "Statistics model"
              : "Statistikmodell"}{" "}
            · {bookNames(m)} {m.quote.toFixed(2)} / {en ? "fair" : "fair"}{" "}
            {m.fair.toFixed(2)} · +{((m.quote / m.fair - 1) * 100).toFixed(2)} %
          </p>
        ))
      ) : (
        <p>
          {error ||
            (ind.state === "none"
              ? en
                ? "No indication"
                : "Keine Indikation"
              : ind.state === "loading"
              ? en
                ? "Loading market comparison …"
                : "Marktvergleich wird geladen …"
              : en
              ? "Tip comparison incomplete; available individual quotes are shown below"
              : "Tippvergleich unvollständig; verfügbare Einzelquoten stehen unten")}
        </p>
      )}
      <div className="ta-scroll">
        <table>
          <thead>
            <tr>
              {[
                en ? "Selection" : "Auswahl",
                en ? "Results fair" : "Ergebnis fair",
                en ? "Statistics fair" : "Statistik fair",
                en ? "Best quote" : "Bestquote",
                en ? "Average quote" : "Durchschnittsquote",
              ].map((x) => (
                <th key={x}>{x}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {outcomes.map((side, i) => (
              <tr key={side}>
                <td>
                  {side.toUpperCase()}{" "}
                  {["over-under", "asian-handicap"].includes(market)
                    ? line
                    : ""}
                </td>
                {[
                  models.result[i],
                  models.statistic[i],
                  quotes.highest[i],
                  quotes.average[i],
                ].map((v, j) => (
                  <td key={j}>
                    {typeof v === "number"
                      ? v.toLocaleString(en ? "en-GB" : "de-DE", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <small>
        {en
          ? "A market difference does not prove a settings or market error. Historical market inversion is listed separately below."
          : "Eine Marktabweichung beweist keinen Settings- oder Marktfehler. Historische Marktrückrechnung steht unten getrennt."}
      </small>
    </section>
  );
}
